import React, { useState, useEffect, useMemo } from 'react';

import { SwapOutlined, ShopOutlined } from '@ant-design/icons';

import PageHeader from '@shared/PageHeader';
import { Card, Table, Tag, message, Button, Modal, Input, Space, Empty, Spin, Statistic, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useIsStandard } from '@contexts/operator/PlanContext';
import returnReceiptService from '@services/operator/returnReceiptService';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

const { TextArea } = Input;
const { Text } = Typography;

function PendingConvertPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isStandard = useIsStandard();
  const [loading, setLoading] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    fetchPendingReceipts();
  }, []);

  const fetchPendingReceipts = async () => {
    setLoading(true);
    try {
      const responseData = await returnReceiptService.getPendingConvert();

      if (responseData.success) {
        setReceipts(responseData.data ?? []);
      } else {
        setReceipts([]);
        handleApiError(responseData, {
          t,
          fallbackMessage: t('common.error.fetch_failed'),
        });
      }
    } catch (error) {
      setReceipts([]);
      handleNetworkError(error, {
        t,
        fallbackMessage: t('common.error.fetch_failed'),
      });
    } finally {
      setLoading(false);
    }
  };

  // 按貨主分組
  const groupedByMerchant = useMemo(() => {
    const groups = {};
    receipts.forEach((receipt) => {
      const key = receipt.merchant_id;
      if (!groups[key]) {
        groups[key] = {
          merchant_id: receipt.merchant_id,
          merchant_name: receipt.merchant_name || t('common.unknown'),
          receipts: [],
        };
      }
      groups[key].receipts.push(receipt);
    });
    return Object.values(groups);
  }, [receipts, t]);

  // 取得已選取的驗收單
  const selectedReceipts = useMemo(() => {
    return receipts.filter((r) => selectedRowKeys.includes(r.id));
  }, [receipts, selectedRowKeys]);

  // 驗證是否為同一貨主
  const selectedMerchantIds = useMemo(() => {
    const ids = new Set(selectedReceipts.map((r) => r.merchant_id));
    return [...ids];
  }, [selectedReceipts]);

  const isSameMerchant = selectedMerchantIds.length <= 1;

  // 商品彙總（Modal 用）
  const productSummary = useMemo(() => {
    const map = {};
    selectedReceipts.forEach((receipt) => {
      if (receipt.items) {
        receipt.items.forEach((item) => {
          const key = item.product_sku;
          if (!map[key]) {
            map[key] = {
              product_id: item.product_id,
              product_sku: item.product_sku,
              product_name: item.product_name,
              received_quantity: 0,
            };
          }
          map[key].received_quantity += item.received_quantity || item.quantity || 0;
        });
      }
    });
    return Object.values(map);
  }, [selectedReceipts]);

  // 統計數據（Modal 用）
  const totalPcs = useMemo(() => {
    return productSummary.reduce((sum, item) => sum + item.received_quantity, 0);
  }, [productSummary]);

  // 處理選取變更：跨貨主選取時警告
  const handleSelectionChange = (newKeys, records, info) => {
    if (newKeys.length === 0) {
      setSelectedRowKeys([]);
      return;
    }

    // 取得新選取的貨主 ID
    const newSelectedReceipts = receipts.filter((r) => newKeys.includes(r.id));
    const merchantIds = new Set(newSelectedReceipts.map((r) => r.merchant_id));

    if (merchantIds.size > 1) {
      message.warning(t('operator.pages.return_receipts.pending_convert.select_same_merchant'));
      return;
    }

    setSelectedRowKeys(newKeys);
  };

  // 開啟 Modal
  const handleOpenModal = () => {
    if (!isSameMerchant) {
      message.warning(t('operator.pages.return_receipts.pending_convert.select_same_merchant'));
      return;
    }
    setNote('');
    setIsModalVisible(true);
  };

  // 關閉 Modal
  const handleCloseModal = () => {
    setIsModalVisible(false);
    setNote('');
  };

  // 確認轉入庫
  const handleConvertToInbound = async () => {
    setSubmitting(true);
    try {
      const responseData = await returnReceiptService.convertToInbound({
        return_receipt_ids: selectedRowKeys,
        note,
      });

      if (responseData.success) {
        message.success(t('operator.pages.return_receipts.pending_convert.convert_success'));
        setSelectedRowKeys([]);
        handleCloseModal();

        // 導航到新的入庫單
        const inboundId = responseData.data?.inbound_id;
        if (inboundId) {
          navigate(`/inbounds/${inboundId}`);
        } else {
          fetchPendingReceipts();
        }
      } else {
        handleApiError(responseData, {
          t,
          fallbackMessage: t('common.error.operation_failed'),
        });
      }
    } catch (error) {
      handleNetworkError(error, {
        t,
        fallbackMessage: t('common.error.operation_failed'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: t('models.return_receipt.name'),
        dataIndex: 'name',
        key: 'name',
        width: 180,
        render: (val, record) => record.return_order_id ? <Link to={`/return_orders/${record.return_order_id}`}>{val}</Link> : val,
      },
      {
        title: t('models.return_receipt.tracking_number'),
        dataIndex: 'tracking_number',
        key: 'tracking_number',
        width: 180,
        render: (val) => val || '-',
      },
      {
        title: t('models.return_order.name'),
        dataIndex: 'return_order_name',
        key: 'return_order_name',
        width: 180,
        render: (val, record) => record.return_order_id ? <Link to={`/return_orders/${record.return_order_id}`}>{val}</Link> : '-',
      },
      {
        title: t('operator.pages.return_receipts.pending_convert.item_count'),
        dataIndex: 'items',
        key: 'item_count',
        width: 100,
        render: (items) => (items ? items.length : 0),
      },
      {
        title: t('models.return_receipt.verified_at'),
        dataIndex: 'verified_at',
        key: 'verified_at',
        width: 160,
        render: (val) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-'),
      },
    ],
    [t]
  );

  if (loading && receipts.length === 0) {
    return (
      <div>
        <PageHeader items={['return_orders', 'return_orders.pending_convert']} />
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader items={['return_orders', 'return_orders.pending_convert']} />

      {groupedByMerchant.length === 0 ? (
        <Card bordered={false}>
          <Empty description={t('operator.pages.return_receipts.pending_convert.empty')} />
        </Card>
      ) : (
        <>
          {groupedByMerchant.map((group) => (
            <Card
              key={group.merchant_id}
              title={
                isStandard ? null : (
                  <Space>
                    <ShopOutlined />
                    <span>
                      {t('operator.pages.return_receipts.pending_convert.merchant_group')}：{group.merchant_name}
                    </span>
                    <Tag>{group.receipts.length}</Tag>
                  </Space>
                )
              }
              bordered={false}
              style={{ marginBottom: 16 }}
            >
              <Table
                rowSelection={{
                  selectedRowKeys,
                  onChange: handleSelectionChange,
                }}
                columns={columns}
                dataSource={group.receipts}
                rowKey="id"
                loading={loading}
                pagination={false}
                size="middle"
              />
            </Card>
          ))}
        </>
      )}

      {/* 底部操作列 */}
      {selectedRowKeys.length > 0 && (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: '#fff',
            padding: '12px 24px',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 10,
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Tag color="blue">
            {t('operator.pages.return_receipts.pending_convert.selected_count', {
              count: selectedRowKeys.length,
            })}
          </Tag>
          <Button
            type="primary"
            icon={<SwapOutlined />}
            onClick={handleOpenModal}
            disabled={!isSameMerchant}
          >
            {t('operator.pages.return_receipts.pending_convert.convert_button')}
          </Button>
        </div>
      )}

      {/* 轉入庫確認 Modal */}
      <Modal
        title={t('operator.pages.return_receipts.pending_convert.confirm_title')}
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={handleConvertToInbound}
        confirmLoading={submitting}
        okText={t('operator.pages.return_receipts.pending_convert.convert_button')}
        cancelText={t('common.button.cancel')}
        width={600}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {/* 統計數據 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            background: '#fafafa',
            borderRadius: 8,
            padding: '20px 0',
          }}>
            <Statistic
              title={t('operator.pages.return_receipts.pending_convert.receipt_count')}
              value={selectedReceipts.length}
              valueStyle={{ fontSize: 32, fontWeight: 600 }}
            />
            <Statistic
              title={t('operator.pages.return_receipts.pending_convert.sku_count')}
              value={productSummary.length}
              valueStyle={{ fontSize: 32, fontWeight: 600 }}
            />
            <Statistic
              title={t('operator.pages.return_receipts.pending_convert.total_pcs')}
              value={totalPcs}
              valueStyle={{ fontSize: 32, fontWeight: 600, color: '#1677ff' }}
            />
          </div>

          {/* 商品明細 */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {t('operator.pages.return_receipts.pending_convert.product_summary')}
            </Text>
            <Table
              dataSource={productSummary}
              rowKey="product_sku"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'SKU',
                  dataIndex: 'product_sku',
                  key: 'product_sku',
                  width: 150,
                },
                {
                  title: t('models.product.name'),
                  dataIndex: 'product_name',
                  key: 'product_name',
                },
                {
                  title: t('models.inbound_item.received_quantity'),
                  dataIndex: 'received_quantity',
                  key: 'received_quantity',
                  width: 100,
                  align: 'right',
                },
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2} align="right">
                    <Text strong>{t('operator.pages.return_receipts.pending_convert.total')}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Text strong>{totalPcs}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>

          {/* 備註 */}
          <TextArea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('operator.pages.return_receipts.pending_convert.note_placeholder')}
          />
        </Space>
      </Modal>
    </div>
  );
}

export default PendingConvertPage;
